import { Radio } from '@material-ui/core';
import { createSelector } from '@reduxjs/toolkit';
import { Body, Checkbox, Heading } from 'copilot-design-system';
import React, { ChangeEvent, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { BankCardIcon, CreditCardIcon } from 'src/components/icons';
import { PortalConfigContext } from 'src/context';
import { useAppSelector } from 'src/hooks/useStore';
import { usePaymentProcessingFee } from 'src/legacy/components/billing/new/usePaymentProcessingFee';
import { GetDefaultCurrency } from 'src/legacy/components/Formatters';
import { ModalWrapper } from 'src/legacy/components/Modals';
import InfoTooltip from 'src/legacy/components/Tooltip';
import { isValidFeePayer } from 'src/routes/billing/components/SavedPaymentMethodModal';
import { usePrimaryPaymentSource } from 'src/routes/billing/hooks/usePaymentSources';
import { usePaymentMethods } from 'src/routes/billing/invoices/components/InvoiceEditPage/usePaymentMethods';
import { useGetStripeCustomerQuery } from 'src/services/api';
import {
  Currencies,
  PaymentMethodType,
  PaymentPreferences,
} from 'src/services/api/invoicesApi';
import { RootState } from 'src/store';
import { GraySmall, white } from 'src/theme/colors';
import { arrayToMap } from 'src/utils/array';

const selector = createSelector(
  [
    (state: RootState) => state.clients.companies,
    (state: RootState) => state.payments.accountInfo,
    (state: RootState) => state.activeCompany,
  ],
  (companies, accountInfo, activeCompany) => ({
    companies: arrayToMap(companies, 'id'),
    accountInfo,
    activeCompanyId: activeCompany.activeCompanyId ?? '',
  }),
);

enum FeePayer {
  Internal = 'us',
  Client = 'client',
}

type Props = {
  recipientId?: string;
  paymentPreferences: PaymentPreferences;
  onUpdatePaymentPreferences: (preferences: PaymentPreferences) => void;
  isOpen: boolean;
  onHandleClose: () => void;
  onHandleSuccess: () => void;
  positiveAction: boolean;
  totalAmount: number;
};

/**
 *
 * This component is used to manage the payment fees for manual charge invoices and subscriptions
 * It allows users to:
 * - View and select allowed payment methods
 * - Choose who will pay the associated transaction fee.
 *
 */

export const ManageManualPaymentSetting = ({
  isOpen,
  onHandleClose,
  onHandleSuccess,
  positiveAction,
  recipientId,
  paymentPreferences,
  onUpdatePaymentPreferences,
  totalAmount,
}: Props) => {
  const { companies, accountInfo, activeCompanyId } = useAppSelector(selector);
  const { t } = useTranslation();
  const company = companies.get(recipientId || '');

  const portalConfig = useContext(PortalConfigContext);
  const portalName = portalConfig.name;

  const companyId = company ? company.id : activeCompanyId;

  const { data: stripeCustomer } = useGetStripeCustomerQuery({
    companyId,
  });
  const defaultCurrency =
    stripeCustomer?.currency ||
    accountInfo?.default_currency ||
    GetDefaultCurrency();

  const { defaultSource: defaultSourceId, sources } = usePaymentMethods({
    recipientId: recipientId || '',
    companyId,
  });

  const { selectedSource } = usePrimaryPaymentSource({
    defaultSourceId,
    sources,
  });

  const { creditCardFeeDetails, bankAccountFeeDetails } =
    usePaymentProcessingFee({
      amountDue: totalAmount * 100,
      source: selectedSource,
      preferences: paymentPreferences,
    });

  const availablePaymentMethods = useMemo(() => {
    return [
      defaultCurrency.toUpperCase() === Currencies.USD && {
        label: 'ACH',
        value: PaymentMethodType.BankAccount,
        icon: <BankCardIcon style={{ fontSize: 20, color: white }} />,
        feeLabel: bankAccountFeeDetails.feeLabel,
      },
      {
        label: 'Credit Card',
        value: PaymentMethodType.CreditCard,
        icon: <CreditCardIcon style={{ fontSize: 20, color: GraySmall }} />,
        feeLabel: creditCardFeeDetails.feeLabel,
      },
    ].filter(Boolean);
  }, [defaultCurrency, creditCardFeeDetails, bankAccountFeeDetails]);

  /**
   * Method to handle the change of the allowed payment method
   */
  const handleAllowedPaymentMethodChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const { checked, value } = event.target;
    const method = value as PaymentMethodType;

    // Prevent unchecking the last payment method
    if (paymentPreferences.paymentMethodTypes.length === 1 && !checked) return;

    const updatedMethods = checked
      ? [...paymentPreferences.paymentMethodTypes, method]
      : paymentPreferences.paymentMethodTypes.filter(
          (currentMethod) => currentMethod !== method,
        );

    onUpdatePaymentPreferences({
      ...paymentPreferences,
      paymentMethodTypes: updatedMethods,
      absorbTransactionFee: paymentPreferences.absorbTransactionFee,
    });
  };

  /**
   * Handles the change in fee preference for a payment method.
   * - If the user selects 'Us', the payment method is added to the list of methods that will absorb the transaction fee.
   * - If the user selects 'Client', the payment method is removed from this list, meaning the client will pay the fee.
   */
  const handleFeesPreferenceChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    paymentMethod: PaymentMethodType,
  ) => {
    if (isValidFeePayer(event.target.value)) {
      onUpdatePaymentPreferences({
        ...paymentPreferences,
        absorbTransactionFee:
          event.target.value === FeePayer.Client
            ? paymentPreferences.absorbTransactionFee.filter(
                (method) => method !== paymentMethod,
              )
            : [...paymentPreferences.absorbTransactionFee, paymentMethod],
      });
    }
  };

  const feeLabelRenderer = ({
    feeLabel,
    paymentMethod,
  }: {
    feeLabel: string;
    paymentMethod: PaymentMethodType;
  }): string => {
    // check if fee is payed by internal user
    return paymentPreferences.absorbTransactionFee.includes(paymentMethod)
      ? t(($) => $.page_billing.fee_payer.iu, {
          portalName: portalName,
        })
      : t(($) => $.page_billing.fee_payer.cu, {
          feeLabel: feeLabel,
        });
  };

  return (
    <ModalWrapper
      open={isOpen}
      onClose={onHandleClose}
      positiveAction={positiveAction}
      onSuccess={onHandleSuccess}
      successButtonLabel="Save"
      title="Manage payment preferences"
    >
      <div>
        {availablePaymentMethods.map((paymentMethod) => (
          <div
            key={`${paymentMethod.label}-${paymentMethod.value}`}
            className="mb-6"
          >
            <div className="mb-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {paymentMethod.icon}
                <Body className="text-[14px] font-normal text-black-heading">
                  {paymentMethod.label}
                </Body>
              </div>

              <InfoTooltip
                disableHoverListener={
                  paymentPreferences.paymentMethodTypes.length !== 1 ||
                  !paymentPreferences.paymentMethodTypes.includes(
                    paymentMethod.value,
                  )
                }
                label="At least one payment method must be set."
              >
                <div>
                  <Checkbox
                    label=""
                    value={paymentMethod.value}
                    checked={paymentPreferences.paymentMethodTypes.includes(
                      paymentMethod.value,
                    )}
                    onChange={handleAllowedPaymentMethodChange}
                  />
                </div>
              </InfoTooltip>
            </div>
            {paymentPreferences.paymentMethodTypes.includes(
              paymentMethod.value,
            ) && (
              <div className="flex justify-between border border-solid border-non-hover-border rounded-4 p-3 ">
                <div>
                  <Heading className="text-[14px] text-dark-black">
                    Decide who pays the fee
                  </Heading>
                  <Body className="text-[12px] text-gray-small">
                    {feeLabelRenderer({
                      feeLabel: paymentMethod.feeLabel,
                      paymentMethod: paymentMethod.value,
                    })}
                  </Body>
                </div>
                <div>
                  <label className="flex gap-2 items-center justify-end mb-2">
                    <Body className="text-[14px] text-dark-black">Us</Body>
                    <Radio
                      size="small"
                      className="p-0"
                      value={FeePayer.Internal}
                      checked={paymentPreferences.absorbTransactionFee.includes(
                        paymentMethod.value,
                      )}
                      color="primary"
                      onChange={(e) =>
                        handleFeesPreferenceChange(e, paymentMethod.value)
                      }
                    />
                  </label>
                  <label className="flex gap-2 items-center justify-end">
                    <Body className="text-[14px] text-dark-black">
                      {` ${t(($) => $.client, { count: 1 })}`}
                    </Body>
                    <Radio
                      size="small"
                      className="p-0"
                      value={FeePayer.Client}
                      checked={
                        !paymentPreferences.absorbTransactionFee.includes(
                          paymentMethod.value,
                        )
                      }
                      onChange={(e) =>
                        handleFeesPreferenceChange(e, paymentMethod.value)
                      }
                      color="primary"
                    />
                  </label>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </ModalWrapper>
  );
};
